import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Smartphone, 
  MapPin, 
  Clock, 
  User, 
  Phone as PhoneIcon,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Calendar,
  Package,
  LogOut,
  RefreshCw,
  Navigation,
  Bell,
  BarChart2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Engineer Login Component
const EngineerLogin = ({ onLogin }) => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('mobile'); // mobile, otp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const handleSendOtp = async () => {
    if (mobile.length !== 10) {
      setError('Please enter valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      // First check if mobile belongs to a pickup engineer
      const checkResponse = await fetch(`${API}/api/employees/by-mobile/${mobile}`);
      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        if (checkData.employee) {
          // Check if employee is a pickup engineer
          const role = checkData.employee.role || checkData.employee.designation || '';
          const dept = checkData.employee.department || '';
          if (role.toLowerCase().includes('pickup') || 
              role.toLowerCase().includes('engineer') || 
              dept.toLowerCase().includes('buyback')) {
            setOtpSent(true);
            setStep('otp');
          } else {
            setError('This mobile is not registered as a Pickup Engineer');
            return;
          }
        } else {
          // Allow demo login for unregistered engineers
          setOtpSent(true);
          setStep('otp');
        }
      } else {
        // API not available, allow demo mode
        setOtpSent(true);
        setStep('otp');
      }
    } catch (err) {
      // Network error, allow demo mode
      setOtpSent(true);
      setStep('otp');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError('Please enter valid 6-digit OTP');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      // Try to get engineer from database
      const response = await fetch(`${API}/api/employees/by-mobile/${mobile}`);
      if (response.ok) {
        const data = await response.json();
        if (data.employee) {
          const engineerData = {
            ...data.employee,
            employee_id: data.employee.employee_id || data.employee.id,
            logged_in_at: new Date().toISOString()
          };
          localStorage.setItem('engineer', JSON.stringify(engineerData));
          onLogin(engineerData);
          return;
        }
      }
      
      // Fallback: Check available engineers list
      const availableRes = await fetch(`${API}/api/buyback/engineers/available`);
      if (availableRes.ok) {
        const availableData = await availableRes.json();
        const engineers = availableData.engineers || [];
        const matchedEngineer = engineers.find(e => e.mobile === mobile);
        if (matchedEngineer) {
          const engineerData = {
            ...matchedEngineer,
            logged_in_at: new Date().toISOString()
          };
          localStorage.setItem('engineer', JSON.stringify(engineerData));
          onLogin(engineerData);
          return;
        }
      }
      
      // Demo mode - use ENG-001 for test mobile, otherwise generate ID
      const isTestMobile = mobile === '9876543210';
      const mockEngineer = {
        employee_id: isTestMobile ? 'ENG-001' : `ENG-${mobile.slice(-4)}`,
        name: isTestMobile ? 'Test Pickup Engineer' : 'Demo Engineer',
        mobile: mobile,
        role: 'Pickup Engineer',
        is_demo: true,
        logged_in_at: new Date().toISOString()
      };
      localStorage.setItem('engineer', JSON.stringify(mockEngineer));
      onLogin(mockEngineer);
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2b3380] to-[#1a1f4e] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#2b3380] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">ZB</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Pickup Engineer</h1>
          <p className="text-gray-500 mt-1">Sign in to view your pickups</p>
        </div>

        {/* Demo Credentials Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm font-semibold text-blue-800 mb-2">🔐 Demo Login Credentials</p>
          <div className="space-y-1 text-sm text-blue-700">
            <p><span className="font-medium">Mobile:</span> Any 10-digit number (e.g., 9876543210)</p>
            <p><span className="font-medium">OTP:</span> Any 6-digit code (e.g., 123456)</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {step === 'mobile' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">+91</span>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter mobile number"
                  className="w-full pl-14 pr-4 py-4 border border-gray-200 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={handleSendOtp}
              disabled={loading || mobile.length !== 10}
              className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold text-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#1e2560] transition-colors"
            >
              {loading ? 'Sending...' : 'Get OTP'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP sent to +91 {mobile}
              </label>
              <input
                type="tel"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="w-full px-4 py-4 border border-gray-200 rounded-xl text-lg text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                maxLength={6}
              />
              <p className="text-sm text-gray-500 mt-2 text-center">
                Demo: Enter any 6 digits (e.g., 123456)
              </p>
            </div>
            <button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length !== 6}
              className="w-full py-4 bg-[#2b3380] text-white rounded-xl font-semibold text-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#1e2560] transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>
            <button
              onClick={() => setStep('mobile')}
              className="w-full py-3 text-[#2b3380] font-medium"
            >
              ← Change Number
            </button>
          </div>
        )}

        {/* Install PWA hint */}
        <div className="mt-8 p-4 bg-blue-50 rounded-xl">
          <p className="text-sm text-blue-800 text-center">
            💡 <strong>Tip:</strong> Add to Home Screen for quick access
          </p>
        </div>
      </div>
    </div>
  );
};

// Pickup Card Component
const PickupCard = ({ pickup, onClick }) => {
  const getStatusColor = (status) => {
    const colors = {
      'PICKUP_SCHEDULED': 'bg-blue-100 text-blue-700',
      'PICKUP_ASSIGNED': 'bg-yellow-100 text-yellow-700',
      'EN_ROUTE': 'bg-purple-100 text-purple-700',
      'ARRIVED': 'bg-indigo-100 text-indigo-700',
      'PQ_IN_PROGRESS': 'bg-cyan-100 text-cyan-700',
      'PQ_COMPLETED': 'bg-green-100 text-green-700',
      'QUOTE_GENERATED': 'bg-orange-100 text-orange-700',
      'PICKUP_FAILED': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'Flexible';
    return timeStr;
  };

  // Open Google Maps with directions
  const handleNavigate = (e) => {
    e.stopPropagation(); // Prevent card click
    const address = `${pickup.customer_info?.address || ''}, ${pickup.customer_info?.city || ''} ${pickup.customer_info?.pincode || ''}`;
    // Use directions mode for turn-by-turn navigation
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // Call customer
  const handleCall = (e) => {
    e.stopPropagation(); // Prevent card click
    window.location.href = `tel:${pickup.customer_info?.mobile}`;
  };

  return (
    <div 
      onClick={onClick}
      className={`bg-white rounded-xl shadow-sm border p-4 mb-3 active:scale-[0.98] transition-transform cursor-pointer ${
        pickup.status === 'PICKUP_FAILED' ? 'border-red-200 bg-red-50/30' : 'border-gray-100'
      }`}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-800">{pickup.customer_info?.name || 'Customer'}</h3>
          <p className="text-sm text-gray-500">{pickup.lead_id}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(pickup.status)}`}>
          {pickup.status?.replace(/_/g, ' ')}
        </span>
      </div>

      {/* Device Info */}
      <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
        <Smartphone className="w-5 h-5 text-gray-400" />
        <div>
          <p className="font-medium text-gray-800">
            {pickup.device_info?.brand} {pickup.device_info?.model}
          </p>
          <p className="text-xs text-gray-500">{pickup.device_info?.variant || pickup.device_info?.storage}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="font-bold text-green-600">₹{pickup.quoted_price?.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Quoted</p>
        </div>
      </div>

      {/* Time & Location */}
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          <span>{pickup.pickup_scheduled_date || 'Today'}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{formatTime(pickup.pickup_scheduled_time)}</span>
        </div>
      </div>

      {/* Address */}
      <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100">
        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-600 line-clamp-2 flex-1">
          {pickup.customer_info?.address}, {pickup.customer_info?.city} - {pickup.customer_info?.pincode}
        </p>
      </div>

      {/* Quick Actions - Navigation & Call */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
        <button
          onClick={handleNavigate}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-50 text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-100 active:scale-[0.98] transition-all"
          data-testid="navigate-btn"
        >
          <Navigation className="w-4 h-4" />
          Navigate
        </button>
        <button
          onClick={handleCall}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-50 text-green-600 rounded-lg font-medium text-sm hover:bg-green-100 active:scale-[0.98] transition-all"
          data-testid="call-btn"
        >
          <PhoneIcon className="w-4 h-4" />
          Call
        </button>
        <button
          onClick={onClick}
          className="flex items-center justify-center p-2.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition-all"
          data-testid="view-details-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Main Dashboard Component
const EngineerDashboard = ({ engineer, onLogout }) => {
  const navigate = useNavigate();
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today'); // today, upcoming, completed
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showDemoNotice, setShowDemoNotice] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);

  // GPS Tracking - Send location every 30 seconds
  useEffect(() => {
    let watchId = null;
    let intervalId = null;

    const sendLocationToServer = async (position) => {
      try {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          altitude: position.coords.altitude,
          battery_level: navigator.getBattery ? 
            await navigator.getBattery().then(b => Math.round(b.level * 100)) : null,
          is_charging: navigator.getBattery ?
            await navigator.getBattery().then(b => b.charging) : false
        };

        setLastLocation(locationData);

        await fetch(`${API}/api/engineer-tracking/location?engineer_id=${engineer.employee_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(locationData)
        });
      } catch (error) {
        console.error('Error sending location:', error);
      }
    };

    const startTracking = () => {
      if (!navigator.geolocation) {
        console.log('Geolocation not supported');
        return;
      }

      // Request permission and start watching
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationEnabled(true);
          sendLocationToServer(position);
        },
        (error) => {
          console.error('Location error:', error);
          setLocationEnabled(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );

      // Watch position changes
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setLocationEnabled(true);
          sendLocationToServer(position);
        },
        (error) => console.error('Watch error:', error),
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 30000 }
      );

      // Also send periodic updates every 30 seconds
      intervalId = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          sendLocationToServer,
          (error) => console.error('Interval location error:', error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }, 30000);
    };

    startTracking();

    // Cleanup on unmount
    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
      if (intervalId) clearInterval(intervalId);
      
      // Mark engineer as offline
      fetch(`${API}/api/engineer-tracking/offline?engineer_id=${engineer.employee_id}`, {
        method: 'POST'
      }).catch(() => {});
    };
  }, [engineer.employee_id]);

  const fetchPickups = useCallback(async () => {
    try {
      // First try: Get leads from dedicated engineer endpoint
      let response = await fetch(`${API}/api/buyback/engineer/${engineer.employee_id}/leads?include_completed=true`);
      let data = null;
      
      if (response.ok) {
        data = await response.json();
        if (data.leads && data.leads.length > 0) {
          setPickups(data.leads);
          setShowDemoNotice(false);
          setLastRefresh(new Date());
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }
      
      // Fallback: Try the general leads endpoint with filters
      const params = new URLSearchParams();
      if (engineer.employee_id) {
        params.append('pickup_engineer_id', engineer.employee_id);
      }
      
      response = await fetch(`${API}/api/buyback/leads?${params.toString()}`);
      
      if (response.ok) {
        data = await response.json();
        if (data.leads && data.leads.length > 0) {
          setPickups(data.leads);
          setShowDemoNotice(false);
          setLastRefresh(new Date());
          setLoading(false);
          setRefreshing(false);
          return;
        }
      }
      
      // Try getting all scheduled/assigned leads (for demo engineers)
      const allLeadsParams = new URLSearchParams();
      response = await fetch(`${API}/api/buyback/leads?limit=20`);
      if (response.ok) {
        data = await response.json();
        if (data.leads && data.leads.length > 0) {
          // Filter to show only actionable leads
          const actionableLeads = data.leads.filter(l => 
            ['PICKUP_SCHEDULED', 'PICKUP_ASSIGNED', 'QUOTE_GENERATED', 'PQ_COMPLETED'].includes(l.status)
          );
          if (actionableLeads.length > 0) {
            setPickups(actionableLeads);
            setShowDemoNotice(engineer.is_demo || false);
            setLastRefresh(new Date());
            setLoading(false);
            setRefreshing(false);
            return;
          }
        }
      }
      
      // Fallback to demo data if no real leads found
      setShowDemoNotice(true);
      setPickups([
        {
          lead_id: 'BBL-DEMO-00001',
          status: 'PICKUP_SCHEDULED',
          customer_info: {
            name: 'Demo Customer',
            mobile: '9876543210',
            address: '123 MG Road, Koramangala',
            city: 'Bangalore',
            pincode: '560034'
          },
          device_info: {
            brand: 'Apple',
            model: 'iPhone 12',
            variant: '128GB',
            storage: '128GB'
          },
          quoted_price: 17610,
          pickup_scheduled_date: new Date().toISOString().split('T')[0],
          pickup_scheduled_time: '10:00 AM - 12:00 PM',
          is_demo: true
        },
        {
          lead_id: 'BBL-DEMO-00002',
          status: 'PICKUP_ASSIGNED',
          customer_info: {
            name: 'Demo Customer 2',
            mobile: '9876543211',
            address: '45 HSR Layout, Sector 2',
            city: 'Bangalore',
            pincode: '560102'
          },
          device_info: {
            brand: 'Samsung',
            model: 'Galaxy S21',
            variant: '8GB/256GB',
            storage: '256GB'
          },
          quoted_price: 22500,
          pickup_scheduled_date: new Date().toISOString().split('T')[0],
          pickup_scheduled_time: '2:00 PM - 4:00 PM',
          is_demo: true
        }
      ]);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch pickups:', err);
      setShowDemoNotice(true);
      setPickups([
        {
          lead_id: 'BBL-DEMO-00001',
          status: 'PICKUP_SCHEDULED',
          customer_info: {
            name: 'Demo Customer',
            mobile: '9876543210',
            address: '123 Demo Address',
            city: 'Bangalore',
            pincode: '560034'
          },
          device_info: {
            brand: 'Apple',
            model: 'iPhone 12',
            variant: '128GB',
            storage: '128GB'
          },
          quoted_price: 17610,
          pickup_scheduled_date: new Date().toISOString().split('T')[0],
          pickup_scheduled_time: '10:00 AM - 12:00 PM',
          is_demo: true
        }
      ]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [engineer]);

  useEffect(() => {
    fetchPickups();
    
    // Auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchPickups();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchPickups]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPickups();
  };

  const filteredPickups = pickups.filter(p => {
    if (filter === 'completed') return p.status === 'PQ_COMPLETED' || p.status === 'PO_GENERATED' || p.status === 'PAYMENT_COMPLETED';
    if (filter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      const isToday = p.pickup_scheduled_date === today || (p.created_at && p.created_at.startsWith(today));
      const isPending = !['PQ_COMPLETED', 'PO_GENERATED', 'PAYMENT_COMPLETED', 'CANCELLED'].includes(p.status);
      return isPending || isToday;
    }
    return true;
  });

  const stats = {
    total: pickups.length,
    pending: pickups.filter(p => ['PICKUP_SCHEDULED', 'PICKUP_ASSIGNED', 'QUOTE_GENERATED', 'PICKUP_REQUESTED'].includes(p.status)).length,
    completed: pickups.filter(p => ['PQ_COMPLETED', 'PO_GENERATED', 'PAYMENT_COMPLETED'].includes(p.status)).length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#2b3380] text-white px-4 pt-12 pb-6 rounded-b-3xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-blue-200 text-sm">Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}</p>
            <h1 className="text-xl font-bold">{engineer.name}</h1>
            <p className="text-blue-200 text-sm">{engineer.employee_id}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleRefresh}
              className="p-2 bg-white/10 rounded-full"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={onLogout}
              className="p-2 bg-white/10 rounded-full"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-blue-200">Total</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats.pending}</p>
            <p className="text-xs text-blue-200">Pending</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats.completed}</p>
            <p className="text-xs text-blue-200">Done</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-sm p-1 flex">
          {[
            { id: 'today', label: 'Today' },
            { id: 'upcoming', label: 'All' },
            { id: 'completed', label: 'Completed' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === tab.id 
                  ? 'bg-[#2b3380] text-white' 
                  : 'text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Demo Notice */}
      {showDemoNotice && (
        <div className="mx-4 mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800 text-center">
            📋 Showing demo data. Real leads will appear when assigned to you.
          </p>
        </div>
      )}
      
      {/* Last Refresh */}
      {lastRefresh && (
        <div className="px-4 mt-2">
          <p className="text-xs text-gray-400 text-center">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
      )}

      {/* Pickup List */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2b3380]"></div>
          </div>
        ) : filteredPickups.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No pickups found</p>
          </div>
        ) : (
          filteredPickups.map(pickup => (
            <PickupCard 
              key={pickup.lead_id} 
              pickup={pickup}
              onClick={() => navigate(`/engineer/pickup/${pickup.lead_id}`)}
            />
          ))
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-around">
        <button className="flex flex-col items-center text-[#2b3380]">
          <Package className="w-6 h-6" />
          <span className="text-xs mt-1">Pickups</span>
        </button>
        <button 
          onClick={() => navigate('/engineer/performance')}
          className="flex flex-col items-center text-gray-400"
        >
          <BarChart2 className="w-6 h-6" />
          <span className="text-xs mt-1">Performance</span>
        </button>
        <button 
          onClick={() => navigate('/engineer/profile')}
          className="flex flex-col items-center text-gray-400"
        >
          <User className="w-6 h-6" />
          <span className="text-xs mt-1">Profile</span>
        </button>
      </div>
    </div>
  );
};

// Main App Component
const EngineerApp = () => {
  // Initialize engineer from localStorage synchronously
  const [engineer, setEngineer] = useState(() => {
    const stored = localStorage.getItem('engineer');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = (eng) => {
    setEngineer(eng);
  };

  const handleLogout = () => {
    localStorage.removeItem('engineer');
    setEngineer(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2b3380]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!engineer) {
    return <EngineerLogin onLogin={handleLogin} />;
  }

  return <EngineerDashboard engineer={engineer} onLogout={handleLogout} />;
};

export default EngineerApp;
